<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          Calendar Configuration
        </oxd-text>
        <oxd-switch-input
          v-model="configuration.enabled"
          label-position="left"
          option-label="Enable Google Calendar Leave Sync"
        />
      </div>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSubmit">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.clientId"
                label="Google Client ID"
                :rules="clientIdRules"
                :required="configuration.enabled"
              />
              <oxd-text tag="p" class="orangehrm-input-hint">
                Use the OAuth 2.0 Web application client ID from Google Cloud.
              </oxd-text>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.clientSecret"
                type="password"
                label="Google Client Secret"
                :rules="clientSecretRules"
                :required="
                  configuration.enabled && !configuration.hasClientSecret
                "
              />
              <oxd-text tag="p" class="orangehrm-input-hint">
                {{
                  configuration.hasClientSecret
                    ? 'Leave blank to keep the stored client secret.'
                    : 'Paste the client secret from the same Google OAuth app.'
                }}
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.timezoneName"
                label="Timezone"
                :rules="rules.timezoneName"
                placeholder="America/Chicago"
                required
              />
              <oxd-text tag="p" class="orangehrm-input-hint">
                This timezone is used when OrangeHRM creates Google Calendar
                leave events.
              </oxd-text>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                :model-value="configuration.callbackUrl"
                label="Authorized Redirect URI"
                disabled
              />
              <oxd-text tag="p" class="orangehrm-input-hint">
                Copy this exact URL into Google Cloud Console as an authorized
                redirect URI.
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-text tag="p" class="orangehrm-input-hint">
                After this is saved, each employee connects their own Google
                Calendar from My Info &gt; Contact Details.
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                OrangeHRM creates Out of Office leave events on the employee's
                primary Google Calendar and removes them if the leave is later
                cancelled or rejected.
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <div class="orangehrm-actions-group">
            <oxd-button
              type="button"
              display-type="ghost"
              :label="$t('general.reset')"
              @click="onReset"
            />
            <submit-button />
          </div>
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  required,
  shouldNotExceedCharLength,
} from '@ohrm/core/util/validation/rules';
import {OxdSwitchInput} from '@ohrm/oxd';

const defaultConfiguration = () => ({
  enabled: false,
  clientId: '',
  clientSecret: '',
  hasClientSecret: false,
  timezoneName: 'UTC',
  callbackUrl: '',
});

const cloneConfiguration = (configuration) => ({
  ...configuration,
  clientSecret: '',
});

const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const validTimezoneName = (value) => {
  if (!value) {
    return true;
  }

  try {
    Intl.DateTimeFormat(undefined, {timeZone: value});
    return true;
  } catch {
    return 'Enter a valid timezone, such as America/Chicago.';
  }
};

export default {
  components: {
    'oxd-switch-input': OxdSwitchInput,
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/calendar-configuration',
    );

    return {
      http,
    };
  },

  data() {
    const browserTimezone = getBrowserTimezone();

    return {
      browserTimezone,
      isLoading: false,
      configuration: defaultConfiguration(),
      initialConfiguration: defaultConfiguration(),
      rules: {
        timezoneName: [
          required,
          shouldNotExceedCharLength(255),
          validTimezoneName,
        ],
      },
    };
  },

  computed: {
    clientIdRules() {
      const rules = [shouldNotExceedCharLength(255)];
      if (this.configuration.enabled) {
        rules.unshift(required);
      }
      return rules;
    },

    clientSecretRules() {
      const rules = [shouldNotExceedCharLength(255)];
      if (this.configuration.enabled && !this.configuration.hasClientSecret) {
        rules.unshift(required);
      }
      return rules;
    },
  },

  created() {
    this.loadConfiguration();
  },

  methods: {
    normalizeConfiguration(configuration) {
      const normalizedConfiguration = {
        ...defaultConfiguration(),
        ...configuration,
      };

      if (
        normalizedConfiguration.timezoneName === 'UTC' &&
        this.browserTimezone !== ''
      ) {
        normalizedConfiguration.timezoneName = this.browserTimezone;
      }

      return normalizedConfiguration;
    },

    loadConfiguration() {
      this.isLoading = true;
      this.http
        .getAll()
        .then((response) => {
          const configuration = this.normalizeConfiguration(response.data.data);
          this.configuration = cloneConfiguration(configuration);
          this.initialConfiguration = cloneConfiguration(configuration);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    onReset() {
      this.configuration = cloneConfiguration(this.initialConfiguration);
    },

    onSubmit() {
      this.isLoading = true;
      this.http
        .request({
          method: 'PUT',
          data: {
            enabled: this.configuration.enabled,
            clientId: this.configuration.clientId,
            clientSecret: this.configuration.clientSecret,
            timezoneName: this.configuration.timezoneName,
          },
        })
        .then((response) => {
          const configuration = this.normalizeConfiguration(response.data.data);
          this.configuration = cloneConfiguration(configuration);
          this.initialConfiguration = cloneConfiguration(configuration);
          return this.$toast.updateSuccess();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-actions-group {
  display: flex;
  gap: 1rem;
}
</style>
